/**
 * autoShrinkLabel — per-line font-size auto-fit for multi-line labels.
 *
 * Given a container element whose direct children are line spans, walks
 * each line and, if it overflows the container's clientWidth, shrinks
 * that line's font-size until it fits. Each line is measured
 * independently — a two-line label "KICK / DRUMS-EXTENDED" leaves KICK
 * at full size and only shrinks DRUMS-EXTENDED.
 *
 * Contract with the caller:
 *   - Each visual line must be a direct child of the container, wrapped
 *     in its own <span class="face-line"> element.
 *   - Line spans should have display: block and their own font-size
 *     inheritance from the container's base font-size.
 *   - Call `applyAutoShrink(containerEl)` on mount, on label change,
 *     and on container resize (via ResizeObserver).
 *
 * Algorithm (per line):
 *   - Read base font-size from container's computed style at mount.
 *   - Measure line span's scrollWidth vs container's clientWidth.
 *   - If overflow, shrink font-size by ratio, floor at MIN_PX.
 *   - Apply as inline style so subsequent renders can undo cleanly.
 */

const MIN_PX = 8; // gig-legibility floor — smaller than this reads as illegible

export function applyAutoShrink(container: HTMLElement | null | undefined): void {
  if (!container) return;
  const usable = container.clientWidth;
  if (usable <= 0) return; // not laid out yet
  const lines = container.querySelectorAll<HTMLElement>('.face-line');
  const baseFontSize = parseFloat(getComputedStyle(container).fontSize);
  if (!Number.isFinite(baseFontSize) || baseFontSize <= 0) return;

  for (const line of Array.from(lines)) {
    // Reset first so shrinks and grows both work when the label changes.
    line.style.fontSize = '';
    const overflow = line.scrollWidth - usable;
    if (overflow <= 0) continue; // fits — leave at base size
    const ratio = usable / line.scrollWidth;
    const target = Math.max(MIN_PX, Math.floor(baseFontSize * ratio));
    line.style.fontSize = `${target}px`;
  }
}

/**
 * fitPadName - a button grid pad's name, fitted to the pad (DEC-316 / DEC-317, after button-look-study.html's
 * fitLabels). Unlike applyAutoShrink it GROWS as well as shrinks: with `auto` the name is as big as the pad allows
 * (a third of the pad's shorter side, a little less with an icon above it), and a name of two or more words goes onto
 * two lines when that makes it bigger. A fixed size (S / M / L / XL) is kept unless the name would run off the pad.
 * Never under 9 px.
 *
 * Measures each candidate line ONCE at 100 px in a hidden clone of the label (same classes, so the skin's face,
 * weight, tracking and case apply) and scales: text width is linear in font size. Sets the label's font size inline
 * and returns the lines to show, or null when the pad is not laid out yet.
 */
const FIT_MIN_PX = 9;
export function fitPadName(
  cell: HTMLElement, label: HTMLElement, text: string, auto: boolean, iconAbove: boolean,
): string[] | null {
  const cs = getComputedStyle(cell);
  const w = cell.clientWidth - parseFloat(cs.paddingLeft) - parseFloat(cs.paddingRight) - 2;
  let h = cell.clientHeight - parseFloat(cs.paddingTop) - parseFloat(cs.paddingBottom);
  if (w <= 0 || h <= 0) return null;
  // Room left after the other in-flow parts (an icon above, the edit-mode CC / channel lines), each with the 2 px gap.
  for (const c of Array.from(cell.children) as HTMLElement[]) {
    if (c === label) continue;
    const s = getComputedStyle(c);
    if (s.position === 'absolute' || s.display === 'none') continue;
    h -= c.getBoundingClientRect().height / (cell.getBoundingClientRect().height / cell.clientHeight || 1) + 2;
  }
  label.style.fontSize = '';
  const base = auto
    ? Math.round(Math.min(cell.clientWidth, cell.clientHeight) * (iconAbove ? 0.28 : 0.34))
    : parseFloat(getComputedStyle(label).fontSize);

  const own = splitLabelLines(text);
  const candidates: string[][] = [own];
  const words = text.trim().split(/\s+/);
  if (own.length === 1 && words.length > 1) {
    let best: { m: number; lines: string[] } | null = null;
    for (let s = 1; s < words.length; s++) {
      const a = words.slice(0, s).join(' '), b = words.slice(s).join(' ');
      const m = Math.max(a.length, b.length);
      if (!best || m < best.m) best = { m, lines: [a, b] };
    }
    if (best) candidates.push(best.lines);
  }

  const probe = label.cloneNode(false) as HTMLElement;
  probe.removeAttribute('data-name-size');
  probe.style.cssText = 'position:absolute;visibility:hidden;left:0;top:0;max-width:none;overflow:visible;font-size:100px';
  cell.appendChild(probe);
  const lineHeight = parseFloat(getComputedStyle(label).lineHeight) / parseFloat(getComputedStyle(label).fontSize) || 1.1;
  const fits = candidates.map((lines) => {
    probe.replaceChildren(...lines.map((l) => { const sp = document.createElement('span'); sp.className = 'face-line'; sp.textContent = l || ' '; return sp; }));
    const widest = Math.max(...Array.from(probe.children).map((c) => (c as HTMLElement).offsetWidth), 1);
    const byW = (w / widest) * 100;
    const byH = h / (lines.length * lineHeight);
    const fs = Math.min(base, byW, byH);
    return { lines, fs: Math.max(FIT_MIN_PX, Math.floor(fs * 2) / 2), fits: fs >= FIT_MIN_PX };
  });
  probe.remove();

  const one = fits[0]!;
  const two = fits[1];
  const pick = two && ((two.fits && !one.fits) || (two.fits && two.fs >= one.fs + 1.5)) ? two : one;
  if (auto || pick.fs < base) label.style.fontSize = `${pick.fs}px`;
  return pick.lines;
}

/** Split a label string into an array of lines. Handles \n, \r\n, and
 *  falls back to a single-line array. Empty strings return []. */
export function splitLabelLines(label: string | undefined | null): string[] {
  if (!label) return [];
  return label.split(/\r?\n/);
}
